import { Box, Typography } from "@mui/material"
import type { FC } from "react"

import { SALES_OPPORTUNITY_STATUS_TO_LABEL } from "../../constants"
import type { SalesOpportunitySearchParams } from "../../schemas"

import { FilterChip } from "@/components/common/Filters"
import type { SalesPipelineStatusGql } from "@/graphql/codegen/graphql"
import { formatDate } from "@/utils/date"

interface SalesOpportunityFilterChipsProps {
  searchParams: SalesOpportunitySearchParams
  customerNameById: Record<string, string>
  userNameById: Record<string, string>
  onRemove: (key: keyof SalesOpportunitySearchParams, value?: string | number) => void
}

const shortId = (id: string): string => id.slice(0, 8)

const formatDateRange = (from?: string, to?: string): string => {
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`
  if (from) return `from ${formatDate(from)}`
  if (to) return `until ${formatDate(to)}`
  return ""
}

export const SalesOpportunityFilterChips: FC<SalesOpportunityFilterChipsProps> = ({
  searchParams,
  customerNameById,
  userNameById,
  onRemove,
}) => {
  const companyIds = searchParams.companyIds ?? []
  const createdByUserIds = searchParams.createdByUserIds ?? []
  const statuses = (searchParams.statuses ?? []) as SalesPipelineStatusGql[]
  const { quoteDateFrom, quoteDateTo } = searchParams

  const hasQuoteDateChip = Boolean(quoteDateFrom || quoteDateTo)
  const hasActiveFilters =
    companyIds.length > 0 || createdByUserIds.length > 0 || statuses.length > 0 || hasQuoteDateChip

  if (!hasActiveFilters) return null

  const handleQuoteDateRemove = () => {
    onRemove("quoteDateFrom")
    onRemove("quoteDateTo")
  }

  return (
    <Box className='flex flex-wrap items-center gap-2' data-testid='sales-opportunity.filter-chips'>
      <Typography variant='body1' className='text-gray-700'>
        Applied filters:
      </Typography>

      {companyIds.map((id) => (
        <FilterChip
          key={id}
          label={`Customer: ${customerNameById[id] ?? `#${shortId(id)}`}`}
          onDelete={() => onRemove("companyIds", id)}
          testId={`sales-opportunity.filter-chips.customer-${id}`}
        />
      ))}

      {createdByUserIds.map((id) => (
        <FilterChip
          key={id}
          label={`Created By: ${userNameById[id] ?? `#${shortId(id)}`}`}
          onDelete={() => onRemove("createdByUserIds", id)}
          testId={`sales-opportunity.filter-chips.created-by-${id}`}
        />
      ))}

      {hasQuoteDateChip && (
        <FilterChip
          label={`Quote: ${formatDateRange(quoteDateFrom, quoteDateTo)}`}
          onDelete={handleQuoteDateRemove}
          testId='sales-opportunity.filter-chips.quote-date'
        />
      )}

      {statuses.map((value) => (
        <FilterChip
          key={value}
          label={`Status: ${SALES_OPPORTUNITY_STATUS_TO_LABEL[value]}`}
          onDelete={() => onRemove("statuses", value)}
          testId={`sales-opportunity.filter-chips.status-${value}`}
        />
      ))}
    </Box>
  )
}
